import { Stack, type Theme, useMediaQuery } from '@mui/material';
import { BOTTOM_BAR_HEIGHT } from '/@/components/app-navbar/bottombar';
import RecommendationActions from '/@/components/recommendation-create/bayer/actions';
import RecommendationLockup from '/@/components/recommendation-create/bayer/lockup';
import RecommendationDesktopSidebar from '/@/components/recommendation-create/bayer/sidebar/desktop';
import RecommendationMobileSidebar from '/@/components/recommendation-create/bayer/sidebar/mobile';
import Spinner from '/@/components/spinner';
import type { Step } from '/@/types/recommendation-create';

function RecommendationCreate({
    steps,
    activeStep,
    setActiveStep,
    isLoading,
    areActionsLoading,
    height,
}: {
    steps: Step[];
    activeStep: number;
    setActiveStep: (activeStep: number) => void;
    isLoading?: boolean;
    areActionsLoading?: boolean;
    height?: string;
}) {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const isAppMobileLayout = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const controlledSetActiveStep = (stepIndex: number) => {
        if (activeStep > stepIndex) {
            setActiveStep(stepIndex);
        }
    };
    if (isLoading) {
        return (
            <Stack height='100%' alignItems='center' justifyContent='center'>
                <Spinner />
            </Stack>
        );
    }
    return (
        // On mobile, AppContent's shell stacks its Box in a column flex container, which only
        // stretches the cross axis (width) — so a plain `height: 100%` collapses to the content's
        // own auto height instead of the real viewport, breaking the internal scroll area and the
        // bottom action bar's positioning. Anchor to the actual viewport height there instead.
        // Below `sm` the shell also pads its Box by the BottomBar's height, so the wizard gives
        // that back — otherwise the shell itself scrolls on top of the wizard's own scroll area.
        <Stack
            height={
                height || (isAppMobileLayout ? `calc(100dvh - ${BOTTOM_BAR_HEIGHT})` : isMobile ? '100dvh' : '100%')
            }
            position='relative'
        >
            <Stack direction={isMobile ? 'column' : 'row'} flexGrow={1} overflow='hidden'>
                {isMobile ? (
                    <RecommendationMobileSidebar
                        steps={steps}
                        activeStep={activeStep}
                        setActiveStep={controlledSetActiveStep}
                    />
                ) : (
                    <Stack>
                        <RecommendationDesktopSidebar
                            steps={steps}
                            activeStep={activeStep}
                            setActiveStep={controlledSetActiveStep}
                        />
                    </Stack>
                )}
                <Stack px={isMobile ? 2 : 4} overflow='scroll' flexGrow={1}>
                    {steps[activeStep] && (
                        <RecommendationLockup
                            recommendationType={steps[activeStep].recommendationType || ''}
                            title={steps[activeStep].title || ''}
                            subtitle={steps[activeStep].subtitle || ''}
                        />
                    )}
                    {steps[activeStep]?.content}
                </Stack>
            </Stack>
            <RecommendationActions
                steps={steps}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                isLoading={areActionsLoading}
            />
        </Stack>
    );
}

export default RecommendationCreate;
